import {useEffect} from 'react'
import {Util} from 'yougile-util'
import {DataObjectId, Hub} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {ParentTasksStore} from '@components/right-panel/checklists-tab/subtasks-new/parent-tasks/parent-tasks-store'
import {useForceUpdate, useMemoOne, useSyncEvent} from '@containers/hooks'
import {Sticker} from '@extensions/sticker/sticker'
import {getProjectByTask} from '@extensions/task'
import {getUserIdsInProjectByTask} from '../../../crm-activities-view/activities-data/get-user-ids-in-project-by-board'

interface UseDealDetailsOptions {
  subtitle: string
  organization: Hub | undefined
  contactPerson: Hub | undefined
  managerUserId: DataObjectId
  availableUsers: DataObjectId[]
  onChooseManager: (chosen: DataObjectId[]) => void
}

export const useDealDetails = (deal: Hub): UseDealDetailsOptions => {
  const {project} = getProjectByTask(deal.id) ?? {}

  if (!project) {
    throw new Error('Project not found')
  }

  const parentTasksStore = useMemoOne(
    () => new ParentTasksStore(deal.id, false),
    [deal]
  )
  useEffect(() => parentTasksStore.dispose, [parentTasksStore])

  const {organization, contactPerson} = useMemoOne(() => {
    let organization: Hub | undefined
    let contactPerson: Hub | undefined

    for (const parentId of parentTasksStore.state.parentTaskIds) {
      const parentTask = Sync.get(project).getHub(parentId)
      if (!parentTask) {
        continue
      }

      if (
        parentTask.location ===
        project?.data?.projectSettings?.organizationsLocation
      ) {
        organization = parentTask
        continue
      } else if (
        parentTask.location ===
        project?.data?.projectSettings?.contactPersonsLocation
      ) {
        contactPerson = parentTask
        continue
      }

      if (contactPerson && organization) {
        break
      }
    }

    return {organization, contactPerson}
  }, [parentTasksStore.state.parentTaskIds])

  const forceUpdate = useForceUpdate()

  useSyncEvent(
    'custom',
    {
      filter: () => [deal.id, project?.id].filter(Util.isDefined),
      companyId: deal.companyId
    },
    forceUpdate,
    [deal, project, parentTasksStore]
  )

  const userSticker = Sticker.getUserSticker(deal)
  const getManagerUserId = (): DataObjectId | undefined => {
    const value = deal.stickers?.[userSticker.id]
    return Array.isArray(value) ? value[0] : value
  }

  const onChooseManager = ([userId]: DataObjectId[]): void => {
    const current = deal.stickers?.[userSticker.id]?.[0]

    if (current === userId) {
      Sticker.setUsers(deal, [], deal.location)
    } else if (PermissionsChecker.checkAssignUser(deal, userId)) {
      Sticker.setUsers(deal, [userId], deal.location)
    }

    deal.commit()
  }

  const availableUsers = getUserIdsInProjectByTask(deal)
  const subtitle = organization?.title ?? contactPerson?.title ?? ''

  return {
    subtitle,
    organization,
    contactPerson,
    managerUserId: getManagerUserId(),
    onChooseManager,
    availableUsers
  }
}
